import * as React from 'react';
import ResolveAuthenticationModes from '@Application/Security/Sso/Commands/ResolveAuthenticationModes';
import resolveAuthenticationModes from '@Application/Security/Sso/resolveAuthenticationModes';
import EmailFormData from '@UI/Pages/Authentication/Login/FormData/EmailFormData';
import loginStepSignal, { goToCredentialsStep } from '@UI/Pages/Authentication/Login/Signals/loginStepSignal';
import { useDispatch } from '@verclam/synapse';
import { Button, Form, InputText } from '@verclam/ui-kit';
import { fieldMarginStyles } from '@Guidelines/Theme/commonStyles';

/** Step 1: the address alone. Submitting resolves which modes the domain allows. */
const IdentificationStep: React.FC = () => {
	const dispatch = useDispatch();

	// Seeds from the signal so a return trip from step 2 (`goToIdentificationStep`) reopens with the
	// address already typed, even though this component was unmounted while step 2 was showing.
	const [formData, setFormData] = React.useState(() =>
		EmailFormData.createEmpty().setEmail(loginStepSignal.peek().email),
	);
	const [isResolvingModes, setIsResolvingModes] = React.useState(false);

	const handleEmailChange = React.useCallback((value: string) => {
		setFormData((current) => current.setEmail(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		setIsResolvingModes(true);

		dispatch(resolveAuthenticationModes(new ResolveAuthenticationModes(formData.email)))
			.then((action) => {
				if (action.type !== 'AUTHENTICATION_MODES_RESOLVED') {
					return;
				}

				goToCredentialsStep(formData.email);
			})
			// Resolution failed and the server could not tell which modes the domain allows: the only
			// silent fallback of this screen, and a harmless one — a password-only step 2 opens, and the
			// server will reject the password anyway if the account turns out to be SSO-exclusive.
			.catch(() => goToCredentialsStep(formData.email))
			.finally(() => setIsResolvingModes(false));
	}, [dispatch, formData]);

	return (
		<Form onSubmit={handleSubmit}>
			<InputText
				name="email"
				label="Adresse e-mail"
				floatLabel
				type="email"
				autoComplete="username"
				autoFocus
				required
				value={formData.email}
				onChange={handleEmailChange}
				errorMessage={formData.getValidationMessage('email')}
				isFormFieldValid={formData.isValid('email')}
			/>
			<Button type="submit" label="Continuer" loading={isResolvingModes} css={fieldMarginStyles} />
		</Form>
	);
};

export default IdentificationStep;
